import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const DonationSection = () => {
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const predefinedAmounts = [25, 50, 100, 250, 500];

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount("");
  };

  const handleCustomAmount = (value: string) => {
    setCustomAmount(value);
    setSelectedAmount(0);
  };

  const handleDonation = async () => {
    const amount = customAmount ? parseFloat(customAmount) : selectedAmount;
    
    try {
      // Validate amount
      const { donationSchema } = await import('@/lib/validations');
      donationSchema.parse({ amount });
    } catch (error: any) {
      toast({
        title: "Monto inválido",
        description: error?.issues?.[0]?.message || "Por favor selecciona o ingresa un monto válido para donar.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Create Stripe payment link using Supabase edge function
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: { 
          amount: Math.round(amount * 100) // Convert to cents
        }
      });

      if (error) throw error;
      
      if (data?.url) {
        // Open Stripe payment page in new tab
        window.open(data.url, '_blank');
      } else {
        throw new Error('No se pudo crear el enlace de pago');
      }
    } catch (error) {
      console.error('Donation error:', error);
      toast({
        title: "Error al procesar donación",
        description: "No se pudo procesar tu donación. Por favor intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="donation-section" className="py-20 bg-gradient-primary relative overflow-hidden">
      <div className="absolute inset-0 bg-primary/5"></div>
      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Tu donación 
            <span className="text-accent-orange"> salva vidas</span>
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Cada donación nos acerca un paso más a encontrar curas para las enfermedades neurológicas. 
            Tu apoyo hace posible nuestra investigación.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="shadow-strong border-0 backdrop-blur-sm bg-card/95 dark:bg-card/95">
            <CardHeader>
              <CardTitle className="text-2xl text-center text-foreground">
                Hacer una Donación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-3 block">
                  Selecciona un monto:
                </label>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  {predefinedAmounts.map((amount) => (
                    <Button
                      key={amount}
                      variant={selectedAmount === amount ? "default" : "outline"}
                      onClick={() => handleAmountSelect(amount)}
                      className={`py-3 ${
                        selectedAmount === amount 
                          ? "bg-primary hover:bg-primary-dark text-white shadow-medium" 
                          : "hover:bg-primary/10 hover:border-primary"
                      }`}
                    >
                      €{amount}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  O ingresa un monto personalizado:
                </label>
                <Input
                  type="number"
                  placeholder="Monto en EUR"
                  value={customAmount}
                  onChange={(e) => handleCustomAmount(e.target.value)}
                  className="text-lg py-3"
                />
              </div>

              <div className="bg-accent p-4 rounded-lg">
                <p className="text-sm text-muted-foreground text-center">
                  Con <span className="font-semibold text-foreground">
                    €{customAmount || selectedAmount}
                  </span> podemos financiar{" "}
                  {customAmount || selectedAmount >= 250 
                    ? "una semana completa de investigación en laboratorio" 
                    : customAmount || selectedAmount >= 100
                    ? "análisis avanzados de muestras neurológicas"
                    : "materiales esenciales para nuestros experimentos"
                  }
                </p>
              </div>

              <Button 
                size="lg" 
                className="w-full bg-secondary hover:bg-secondary-light text-white py-4 text-lg font-semibold shadow-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleDonation}
                disabled={isLoading}
              >
                {isLoading ? "Procesando..." : `Donar €${customAmount || selectedAmount}`}
              </Button>

              <div className="mt-6 p-4 bg-muted/50 rounded-lg text-left space-y-4">
                <h4 className="font-semibold text-foreground text-sm">Descubre todas las ventajas de donar a Fundación NeuroResearch</h4>
                
                <p className="text-xs text-muted-foreground">
                  Tanto si eres persona física como persona jurídica, tus donaciones a la Fundación NeuroResearch pueden desgravarse en la declaración de la renta o en el impuesto de sociedades en solamente para España. Nuestra entidad está acogida a la Ley 49/2002, de régimen fiscal de las entidades sin fines lucrativos y de incentivos al mecenazgo. Además, el Real Decreto-Ley 6/2023 ha mejorado las deducciones por donaciones a ONG, por lo que ahora colaborar tiene aún más ventajas.
                </p>

                <div>
                  <h5 className="font-medium text-foreground text-xs mb-1">Deducciones para personas físicas (IRPF)</h5>
                  <p className="text-xs text-muted-foreground mb-2">El porcentaje que puedes deducir depende de la cantidad y de si colaboras de forma continuada:</p>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                    <li>Hasta 250 € al año → deducción del 80%.</li>
                    <li>Cantidad que supere los 250 € → deducción del 40%.</li>
                    <li>Si donas durante 3 años o más y mantienes o aumentas tu aportación → el porcentaje sobre la parte que exceda los 250 € sube al 45%.</li>
                  </ul>
                </div>

                <div>
                  <h5 className="font-medium text-foreground text-xs mb-1">Ejemplos prácticos:</h5>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                    <li>Si donas 250 €, Hacienda te devuelve 200 €, así que tu coste real será de 50 €.</li>
                    <li>Si donas 400 €: Primeros dos años → deducción de 260 €.</li>
                    <li>A partir del tercer año → deducción de 267,5 €.</li>
                  </ul>
                  <p className="text-xs text-muted-foreground mt-2 italic">
                    Recuerda: Si tu comunidad autónoma tiene deducciones adicionales o regímenes forales propios, el beneficio fiscal puede ser aún mayor.
                  </p>
                </div>

                <div>
                  <h5 className="font-medium text-foreground text-xs mb-1">Deducciones para empresas (Impuesto sobre Sociedades)</h5>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                    <li>40% de deducción general.</li>
                    <li>50% si la donación es recurrente (tres años o más).</li>
                    <li>Límite: 15% de la base imponible.</li>
                    <li>Si superas el límite, el exceso se puede aplicar en los 10 años siguientes.</li>
                  </ul>
                </div>

                <p className="text-xs text-primary font-medium">
                  Si quieres apoyarnos, ponte en contacto.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mt-16 text-white text-center">
          <div>
            <div className="text-3xl font-bold mb-2">€2.5M</div>
            <div className="text-white/80">Recaudado en 2025</div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-2">1,200+</div>
            <div className="text-white/80">Donantes activos</div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-2">95%</div>
            <div className="text-white/80">Va directo a investigación</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DonationSection;